/**
 * "Recent data comparisons" store — Task B4 (Soporte3, F07).
 *
 * Mirrors `../../schema-compare/store/recentSchemaCompareStore.ts`'s shape
 * (zustand + persist, 20-entry cap, dedupe-and-move-to-front), with one
 * deliberate divergence: Schema Compare records when its setup dialog is
 * CONFIRMED; this store is recorded from `DataCompareTab`'s successful
 * `onComplete` callback — i.e. only once a comparison has actually RUN — see
 * that file for why. The entry id is derived from the source/target TABLE
 * identity only (not the key/value mappings), so re-running the same table
 * pair with different mappings replaces the existing entry instead of
 * accumulating a near-duplicate.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  DataCompareConnectionEndpoint,
  DataCompareKeyMapping,
  DataCompareOptions,
  DataCompareValueMapping,
} from '../../core/types/workspaceTypes';
import { createGuardedJsonStorage } from '../../../../core/storage/guardedJsonStorage';
import { showPersistWriteFailureToast } from '../../../../components/ui/persistWriteFailureToast';

const MAX_RECENT_ENTRIES = 20;

export type RecentDataCompareEntry = {
  /** Stable identifier derived from the source/target table identity — used for React keys and removal. */
  id: string;
  source: DataCompareConnectionEndpoint;
  target: DataCompareConnectionEndpoint;
  keyMappings: DataCompareKeyMapping[];
  valueMappings: DataCompareValueMapping[];
  options?: DataCompareOptions;
  /** Snapshotted display values so the entry still renders if a connection is later renamed/removed. */
  sourceConnectionName?: string;
  sourceServerHost?: string;
  targetConnectionName?: string;
  targetServerHost?: string;
  comparedAt: Date;
};

export type RecordRecentDataCompareArgs = Omit<RecentDataCompareEntry, 'id' | 'comparedAt'>;

type RecentDataCompareState = {
  entries: RecentDataCompareEntry[];
  record: (args: RecordRecentDataCompareArgs) => void;
  remove: (id: string) => void;
  clear: () => void;
};

function buildEntryId(
  source: DataCompareConnectionEndpoint,
  target: DataCompareConnectionEndpoint,
): string {
  return [
    source.connectionId,
    source.database,
    source.schema,
    source.table,
    target.connectionId,
    target.database,
    target.schema,
    target.table,
  ].join('|');
}

export const useRecentDataCompareStore = create<RecentDataCompareState>()(
  persist(
    (set) => ({
      entries: [],

      record: (args) => {
        // Need a complete table identity on both sides to be meaningful.
        if (!args.source.connectionId || !args.source.database || !args.source.table) return;
        if (!args.target.connectionId || !args.target.database || !args.target.table) return;

        const id = buildEntryId(args.source, args.target);
        const entry: RecentDataCompareEntry = {
          ...args,
          id,
          comparedAt: new Date(),
        };

        set((state) => {
          const without = state.entries.filter((e) => e.id !== id);
          return { entries: [entry, ...without].slice(0, MAX_RECENT_ENTRIES) };
        });
      },

      remove: (id) => {
        set((state) => ({ entries: state.entries.filter((e) => e.id !== id) }));
      },

      clear: () => {
        set({ entries: [] });
      },
    }),
    {
      name: 'sql-studio-recent-data-compare',
      storage: createGuardedJsonStorage({ onFailure: showPersistWriteFailureToast('your recent data comparisons', { plural: true }) }),
      partialize: (state) => ({ entries: state.entries }),
      onRehydrateStorage: () => (state) => {
        if (state?.entries) {
          state.entries = state.entries.map((e) => ({
            ...e,
            comparedAt: new Date(e.comparedAt),
          }));
        }
      },
    },
  ),
);
